import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import * as signalR from '@microsoft/signalr';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import TicketChatHeader from './componentes/TicketChatHeader';
import TicketCustomerSidebar from './componentes/TicketCustomerSidebar';
import { TicketMessagesStream } from './componentes/StreamComponents';
import { TicketQuickCommands, SendMessage } from './componentes/SendMessage';
import { normalizeTicketData, createAgentMessage } from './utils';

const TicketDetailView = ({ ticket: propTicket, onBack, onTicketUpdated, onTicketDetailLoaded }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const ticket = normalizeTicketData(propTicket);
  const { showErrorToast, showSuccessToast } = useToast();

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [quickCommands, setQuickCommands] = useState([]);
  const { token, nombreCompleto, idUsuario } = useToken();

  const [ticketDetail, setTicketDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);
  const hubConnectionRef = useRef(null);
  const messageInputRef = useRef(null);
  const lastFetchedTicketIdRef = useRef(null);

  // Estado para colaboradores / participantes del ticket
  const [participants, setParticipants] = useState([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const PAGE_SIZE = 30;

  // Cargar participantes del ticket
  const fetchParticipants = useCallback(async () => {
    const activeTicketId = ticket?.idTicket || ticket?.id;
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    setIsLoadingParticipants(true);
    try {
      const resp = await fetch(`/api/SupportEngine/GetTicketParticipants?idTicket=${activeTicketId}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const data = result.data || (Array.isArray(result) ? result : []);
        setParticipants(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al consultar participantes:', err);
    } finally {
      setIsLoadingParticipants(false);
    }
  }, [ticket?.idTicket, ticket?.id, token]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  // 1. Cargar información de cabecera y cliente del ticket
  const fetchTicketDetail = async (silent = false) => {
    const activeTicketId = ticket?.idTicket || ticket?.id;
    if (!activeTicketId || activeTicketId === 'undefined') {
      setIsLoadingDetail(false);
      return null;
    }
    if (!silent) setIsLoadingDetail(true);
    try {
      const response = await fetch(`/api/SupportEngine/GetTicketDetail/${activeTicketId}`, {
        headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        if (data) {
          setTicketDetail(data);
          if (onTicketDetailLoaded) {
            onTicketDetailLoaded(data);
          }
          return data;
        }
      }
    } catch (err) {
      console.error('Error al cargar detalle del ticket:', err);
    } finally {
      if (!silent) setIsLoadingDetail(false);
    }
    return null;
  };

  const fetchTicketDetailRef = useRef(fetchTicketDetail);
  fetchTicketDetailRef.current = fetchTicketDetail;

  const ticketDetailRef = useRef(ticketDetail);
  ticketDetailRef.current = ticketDetail;

  const onTicketUpdatedRef = useRef(onTicketUpdated);
  onTicketUpdatedRef.current = onTicketUpdated;

  const markCurrentAsRead = useCallback((ticketIdToMark) => {
    const idNum = Number(ticketIdToMark);
    if (!idNum) return;
    try {
      const stored = localStorage.getItem('support_engine_read_tickets');
      const currentMap = stored ? JSON.parse(stored) : {};
      currentMap[idNum] = new Date().toISOString();
      localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
    } catch (e) {
      console.warn('Error al marcar ticket leido en localStorage:', e);
    }
    window.dispatchEvent(
      new CustomEvent('akasia:support:ticket_read', {
        detail: { idTicket: idNum }
      })
    );
  }, []);

  useEffect(() => {
    const activeTicketId = ticket?.idTicket || ticket?.id;
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    markCurrentAsRead(activeTicketId);

    const normalizedId = String(activeTicketId);
    if (lastFetchedTicketIdRef.current === normalizedId) return;
    lastFetchedTicketIdRef.current = normalizedId;

    fetchTicketDetail();

    return () => {
      markCurrentAsRead(activeTicketId);
    };
  }, [ticket?.idTicket, ticket?.id, token, markCurrentAsRead]);

  // Cargar atajos rápidos desde el catálogo (globales y personalizados del usuario)
  const fetchAtajos = async () => {
    try {
      const query = idUsuario ? `?idUsuario=${idUsuario}` : '';
      const response = await fetch(`/api/SupportEngine/GetAtajos${query}`, {
        headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        if (Array.isArray(list)) {
          setQuickCommands(list);
        }
      }
    } catch (err) {
      console.error('Error al cargar atajos de soporte:', err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAtajos();
    }
  }, [token, idUsuario]);

  // 2. Cargar historial de mensajes del chat
  useEffect(() => {
    const activeTicketId = ticket?.idTicket || ticket?.id;
    if (!activeTicketId || activeTicketId === 'undefined') return;

    const fetchMessages = async () => {
      setIsLoading(true);
      setError(null);
      setHasMore(true);
      try {
        const response = await fetch(`/api/SupportEngine/GetTicketMessages/${activeTicketId}?pageSize=${PAGE_SIZE}`, {
          headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token }
        });

        if (response.ok) {
          const result = await response.json();
          const msgs = result.data || result;
          if (Array.isArray(msgs)) {
            const chronological = msgs.slice().reverse();
            setMessages(chronological);
            if (msgs.length < PAGE_SIZE) {
              setHasMore(false);
            }
          }
        } else {
          const errMsg = `Error al cargar mensajes (${response.status})`;
          setError(errMsg);
          showErrorToast(errMsg);
        }
      } catch (err) {
        setError(`Error de red: ${err.message}`);
        showErrorToast(`Error de red: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [ticket?.id, token]);

  // Función para cargar mensajes más antiguos al scrollear hacia arriba
  const handleLoadMore = async () => {
    const activeTicketId = ticket?.idTicket || ticket?.id;
    if (!activeTicketId || activeTicketId === 'undefined' || isLoadingMore || !hasMore || messages.length === 0) return;

    const oldestMessage = messages[0];
    const cursorId = oldestMessage?.idMensaje;
    if (!cursorId) return;

    setIsLoadingMore(true);
    try {
      const msgsRes = await fetch(`/api/SupportEngine/GetTicketMessages/${activeTicketId}?cursor=${cursorId}&pageSize=${PAGE_SIZE}`, {
        headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token }
      });

      if (msgsRes.ok) {
        const result = await msgsRes.json();
        const olderMsgs = result.data || result;
        if (Array.isArray(olderMsgs) && olderMsgs.length > 0) {
          const chronologicalOlder = olderMsgs.slice().reverse();
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.idMensaje));
            const filteredNew = chronologicalOlder.filter(m => !existingIds.has(m.idMensaje));
            return [...filteredNew, ...prev];
          });
          if (olderMsgs.length < PAGE_SIZE) {
            setHasMore(false);
          }
        } else {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('Error al cargar mensajes anteriores:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // 2. Conexión SignalR en tiempo real a la sala del ticket
  useEffect(() => {
    const activeTicketId = ticket?.idTicket || ticket?.id;
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    let isCancelled = false;
    let connection = null;

    const startSignalR = async () => {
      try {
        const resp = await fetch('/api/SupportEngine/GetBaseUrl', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });
        const { baseUrl } = await resp.json();
        if (isCancelled) return;

        const hubUrl = `${baseUrl}/hubs/support-chat`;
        connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token,
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        hubConnectionRef.current = connection;

        await connection.start();
        if (isCancelled) {
          connection.stop().catch(() => {});
          return;
        }

        // Unirse a la sala del ticket
        await connection.invoke('JoinTicketGroup', parseInt(activeTicketId));

        // Escuchar mensajes entrantes en tiempo real
        connection.on('ReceiveMessage', async newMsg => {
          if (!newMsg) return;
          console.log('💬 [TicketDetailView] Mensaje en tiempo real recibido:', newMsg);
          setMessages(prev => {
            // Evitar duplicados si ya existe por idMensaje
            const exists = prev.some(m => m.idMensaje === newMsg.idMensaje || (m.vMediaId && m.vMediaId === newMsg.vMediaId));
            if (exists) return prev;
            return [...prev, newMsg];
          });

          const isFromClient = newMsg.bEsAgente === 0 || newMsg.bEsAgente === false || newMsg.isAgent === false || newMsg.esAgente === false;

          // Si el asesor tiene este ticket abierto en pantalla, mantenerlo como leído
          markCurrentAsRead(activeTicketId);

          // Notificar reactivamente a la lista de tickets para moverlo a la cima
          window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
            detail: {
              idTicket: parseInt(activeTicketId),
              bEsAgente: isFromClient ? 0 : 1,
              isAgent: !isFromClient,
              vTextoMensaje: newMsg.vTextoMensaje || newMsg.textoMensaje || newMsg.messageText || '',
              dUltimaActividad: newMsg.dCreatedDate || newMsg.createdDate || new Date().toISOString(),
              isCurrentlyOpen: true
            }
          }));

          // Solo si el ticket estaba cerrado y un cliente responde (reapertura), refrescamos estado
          const currentStatus = Number(ticketDetailRef.current?.iEstadoTicket ?? ticket?.iEstadoTicket);
          if (isFromClient && currentStatus === 2) {
            if (fetchTicketDetailRef.current) {
              await fetchTicketDetailRef.current();
            }
            if (onTicketUpdatedRef.current) {
              onTicketUpdatedRef.current();
            }
          }
        });
      } catch (err) {
        console.warn('[SignalR SupportChat] No se pudo conectar al WebSocket en tiempo real:', err.message);
      }
    };

    startSignalR();

    return () => {
      isCancelled = true;
      if (connection) {
        connection.invoke('LeaveTicketGroup', parseInt(activeTicketId)).catch(() => {});
        connection.stop().catch(() => {});
      }
    };
  }, [ticket?.idTicket, ticket?.id, token]);

  const customerName = ticketDetail?.vCustomerName || ticket?.customerName;
  const ticketPhone = ticketDetail?.vTelefono || ticket?.vTelefono || ticket?.phone;
  const iEstadoTicket = ticketDetail?.iEstadoTicket !== undefined ? ticketDetail.iEstadoTicket : ticket?.iEstadoTicket;
  const idCatNivelSoporte = ticketDetail?.idCatNivelSoporte !== undefined ? ticketDetail.idCatNivelSoporte : ticket?.idCatNivelSoporte;
  const idCatArea = ticketDetail?.idCatArea || ticket?.idCatArea;
  const createdDate = ticketDetail?.dCreatedDate || ticket?.createdDate || ticket?.dCreatedDate;
  const ultimaActividad = ticketDetail?.dUltimaActividad || ticket?.ultimaActividad || ticket?.dUltimaActividad;
  const idClienteAkasia = ticketDetail?.idClienteAkasia || ticket?.idClienteAkasia;
  const vCreatedUser = ticketDetail?.vCreatedUser || ticket?.vCreatedUser;
  const idUsuarioAsignado = ticketDetail?.idUsuarioAsignado !== undefined ? ticketDetail.idUsuarioAsignado : ticket?.idUsuarioAsignado;
  const vNombreUsuarioAsignado = ticketDetail?.vNombreUsuarioAsignado || ticket?.vNombreUsuarioAsignado;
  const vMotivoCerrado = ticketDetail?.vMotivoCerrado || ticket?.vMotivoCerrado;
  const iTiempoRespuestaMinutos = ticketDetail?.iTiempoRespuestaMinutos !== undefined 
    ? ticketDetail.iTiempoRespuestaMinutos 
    : ticket?.iTiempoRespuestaMinutos;

  const initials = customerName 
    ? customerName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() 
    : '';

  const activeTicket = {
    ...ticket,
    ...(ticketDetail || {}),
    id: ticketDetail?.idTicket || ticket.id || ticket.idTicket,
    idTicket: ticketDetail?.idTicket || ticket.idTicket || ticket.id,
    customerName,
    initials,
    phone: ticketPhone,
    ticketPhone,
    iEstadoTicket,
    idCatNivelSoporte,
    idCatArea,
    createdDate,
    dCreatedDate: createdDate,
    ultimaActividad,
    dUltimaActividad: ultimaActividad,
    idClienteAkasia,
    vCreatedUser,
    idUsuarioAsignado,
    vNombreUsuarioAsignado,
    vMotivoCerrado,
    iTiempoRespuestaMinutos,
    dFechaCierre: ticketDetail?.dFechaCierre || ticket?.dFechaCierre,
    iTiempoResolucionMinutos: ticketDetail?.iTiempoResolucionMinutos !== undefined ? ticketDetail.iTiempoResolucionMinutos : ticket?.iTiempoResolucionMinutos,
    iTiempoCompletadoMinutos: ticketDetail?.iTiempoCompletadoMinutos !== undefined ? ticketDetail.iTiempoCompletadoMinutos : (ticketDetail?.iTiempoResolucionMinutos ?? ticket?.iTiempoResolucionMinutos)
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  // 1. Fetch para asignar responsable inicial al ticket
  const assignInitialResponsible = async (ticketId, userId) => {
    try {
      const resp = await fetch('/api/SupportEngine/AssignTicketAgent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idUsuarioAsignado: userId
        })
      });
      return resp.ok;
    } catch (err) {
      console.error('Error al auto-asignar responsable inicial:', err);
      return false;
    }
  };

  // 2. Fetch para registrar nuevo participante/colaborador
  const addTicketParticipant = async (ticketId, userId) => {
    try {
      const resp = await fetch('/api/SupportEngine/AddTicketParticipant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idUsuario: userId
        })
      });
      return resp.ok;
    } catch (err) {
      console.error('Error al registrar participante:', err);
      return false;
    }
  };

  // Método principal: evalúa condición y solo agrega participante si no lo es ya
  const syncTicketAssignmentAfterMessage = async () => {
    const currentActiveTicketId = activeTicket?.idTicket || activeTicket?.id || ticket?.idTicket || ticket?.id;
    if (!currentActiveTicketId || !idUsuario) return;

    const numericCurrentAssignee = Number(activeTicket?.idUsuarioAsignado);
    const numericCurrentUserId = Number(idUsuario);

    // 1. Si el ticket no tiene responsable asignado aún
    if (!numericCurrentAssignee || numericCurrentAssignee === 0) {
      const updated = await assignInitialResponsible(currentActiveTicketId, numericCurrentUserId);
      if (updated) {
        setTicketDetail(prev => ({
          ...prev,
          idUsuarioAsignado: numericCurrentUserId,
          vNombreUsuarioAsignado: nombreCompleto
        }));
        if (onTicketUpdated) onTicketUpdated();
      }
      return;
    }

    // 2. Si el usuario actual ya es el responsable asignado, no hacemos nada
    if (numericCurrentAssignee === numericCurrentUserId) {
      return;
    }

    // 3. Si el usuario actual ya figura como participante del ticket, no hacemos nada
    const isAlreadyParticipant = participants.some(p => Number(p.idUsuario) === numericCurrentUserId);
    if (isAlreadyParticipant) {
      return;
    }

    // 4. Solo si no es responsable y no está en la lista de participantes, se agrega
    const added = await addTicketParticipant(currentActiveTicketId, numericCurrentUserId);
    if (added) {
      setParticipants(prev => {
        if (prev.some(p => Number(p.idUsuario) === numericCurrentUserId)) return prev;
        return [
          ...prev,
          {
            idTicketParticipante: Date.now(),
            idUsuario: numericCurrentUserId,
            vNombreCompleto: nombreCompleto,
            dCreatedDate: new Date().toISOString()
          }
        ];
      });
      await fetchParticipants();
    }
  };

  const handleSendMessage = async (e, textOverride = null) => {
    if (e) e.preventDefault();
    const messageText = (textOverride !== null ? textOverride : inputText).trim();
    if ((!messageText && !selectedFile) || isSending) return;

    const fileToSend = selectedFile;
    const previewToSend = filePreview;

    setInputText('');
    setSelectedFile(null);
    setFilePreview(null);

    setIsSending(true);

    // Caso 1: Envío de archivo multimedia (foto, video, etc.)
    if (fileToSend) {
      const isImg = fileToSend.type.startsWith('image/');
      const isVid = fileToSend.type.startsWith('video/');
      const tempMediaMsg = {
        idMensaje: Date.now(),
        bEsAgente: 1,
        idCatTipoMensaje: isImg ? 2 : (isVid ? 5 : 6),
        vTextoMensaje: messageText,
        vMediaUrl: previewToSend?.url,
        vMediaMimeType: fileToSend.type,
        vCreatedUser: nombreCompleto || 'Tú',
        dCreatedDate: new Date().toISOString()
      };

      setMessages(prev => [...prev, tempMediaMsg]);

      try {
        const formData = new FormData();
        const currentActiveTicketId = activeTicket.idTicket || activeTicket.id || ticket.idTicket || ticket.id;
        formData.append('file', fileToSend);
        formData.append('idTicket', currentActiveTicketId);
        if (ticketPhone) {
          formData.append('toPhoneNumber', ticketPhone);
        }
        if (messageText) {
          formData.append('caption', messageText);
        }

        const response = await fetch('/api/SupportEngine/SendTicketMedia', {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token
          },
          body: formData
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          showErrorToast(errData.message || 'Error al enviar archivo multimedia por WhatsApp.');
        } else {
          const result = await response.json();
          if (result.data) {
            setMessages(prev => prev.map(m => m.idMensaje === tempMediaMsg.idMensaje ? result.data : m));
          }
          await syncTicketAssignmentAfterMessage();
          const freshDetail = await fetchTicketDetail(true);
          if (onTicketUpdated) onTicketUpdated();
          setHistoryRefreshKey(prev => prev + 1);
          window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
            detail: {
              idTicket: parseInt(currentActiveTicketId),
              vTextoMensaje: messageText || 'Archivo multimedia',
              dUltimaActividad: new Date().toISOString(),
              dFechaRespuestaInicial: freshDetail?.dFechaRespuestaInicial || ticketDetail?.dFechaRespuestaInicial || new Date().toISOString()
            }
          }));
        }
      } catch (err) {
        showErrorToast('Error de red al enviar archivo: ' + err.message);
      } finally {
        setIsSending(false);
      }
      return;
    }

    // Caso 2: Envío de mensaje de texto tradicional
    const tempMsg = createAgentMessage(messageText, nombreCompleto);
    setMessages(prev => [...prev, tempMsg]);

    try {
      const currentActiveTicketId = activeTicket.idTicket || activeTicket.id || ticket.idTicket || ticket.id;
      const response = await fetch('/api/SupportEngine/SendTicketMessage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(currentActiveTicketId),
          toPhoneNumber: ticketPhone,
          message: messageText
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        let errMsg = errData.message || errData.title;
        if (errData.errors) {
          errMsg = Object.values(errData.errors).flat().join(', ');
        }
        showErrorToast(errMsg || 'Error al enviar el mensaje por WhatsApp.');
      } else {
        await syncTicketAssignmentAfterMessage();
        const freshDetail = await fetchTicketDetail(true);
        if (onTicketUpdated) onTicketUpdated();
        setHistoryRefreshKey(prev => prev + 1);
        window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
          detail: {
            idTicket: parseInt(currentActiveTicketId),
            vTextoMensaje: messageText,
            dUltimaActividad: new Date().toISOString(),
            dFechaRespuestaInicial: freshDetail?.dFechaRespuestaInicial || ticketDetail?.dFechaRespuestaInicial || new Date().toISOString()
          }
        }));
      }
    } catch (err) {
      showErrorToast('Error de red al enviar mensaje: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleQuickCommand = (cmdText, sendImmediately = false) => {
    if (sendImmediately) {
      handleSendMessage(null, cmdText);
    } else {
      setInputText(cmdText);
      // Enfocar el input de mensaje y situar cursor al final
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
          const len = (cmdText || '').length;
          messageInputRef.current.setSelectionRange?.(len, len);
        }
      }, 30);
    }
  };

  return (
    <div
      className="d-flex flex-column flex-xl-row border rounded-3 overflow-hidden shadow-none"
      style={{
        height: 'calc(100vh - 120px)',
        minHeight: '550px',
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        borderColor: isDark ? '#233142' : '#e2e8f0'
      }}
    >
      {/* 1. Left / Center Chat Column */}
      <div
        className="flex-grow-1 d-flex flex-column border-end h-100 position-relative"
        style={{
          backgroundColor: isDark ? '#0b141a' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0',
          minWidth: 0
        }}
      >
        {/* Header Component (Fijo y delimitado) */}
        <div className="flex-shrink-0">
          <TicketChatHeader 
            ticket={activeTicket} 
            onBack={onBack} 
            onTicketClosed={async () => {
              // 1. Recargar los detalles completos del ticket desde la base de datos
              await fetchTicketDetail();
              // 2. Disparar la recarga de la línea de tiempo del historial
              setHistoryRefreshKey(prev => prev + 1);
              // 3. Notificar a TicketsView para sincronizar conteos y lista
              if (onTicketUpdated) {
                onTicketUpdated();
              }
            }}
            onTicketUpdated={async () => {
              await fetchTicketDetail();
            }}
          />
        </div>

        {/* Messages Stream Component (Único con scroll vertical) */}
        <TicketMessagesStream 
          messages={messages} 
          isLoading={isLoading} 
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          error={error} 
        />

        {/* Quick Slash Commands Component (Fijo al fondo) */}
        <div className="flex-shrink-0">
          <TicketQuickCommands commands={quickCommands} onSelectCommand={handleQuickCommand} onReload={fetchAtajos} />
        </div>

        {/* Message Composer Component (Fijo al fondo con slash autocompletado) */}
        <div className="flex-shrink-0">
          <SendMessage 
            inputRef={messageInputRef}
            inputText={inputText} 
            setInputText={setInputText} 
            onSendMessage={handleSendMessage}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            filePreview={filePreview}
            setFilePreview={setFilePreview}
            isSending={isSending}
            commands={quickCommands}
          />
        </div>
      </div>

      {/* 2. Right Details Sidebar Component */}
      <TicketCustomerSidebar 
        ticket={activeTicket} 
        isLoadingDetail={isLoadingDetail}
        participants={participants}
        isLoadingParticipants={isLoadingParticipants}
        onReloadParticipants={fetchParticipants}
        historyRefreshKey={historyRefreshKey}
        onTicketUpdated={async () => {
          await fetchTicketDetail();
          setHistoryRefreshKey(prev => prev + 1);
          if (onTicketUpdated) onTicketUpdated();
        }} 
      />
    </div>
  );
};

export default TicketDetailView;
